/**
 * Types for PQ (Price Quotation) Ingestion
 *
 * Used for uploading and processing supplier price lists
 */

import { AvailabilityTypeEnum } from "./generalTypes";

/**
 * PQ Candidate status constants
 */
export const PQCANDIDATE_STATUS = {
  IN_REVIEW: "IN_REVIEW",
  NOT_CREATED: "NOT_CREATED",
  CREATED: "CREATED",
  FAILED: "FAILED",
} as const;

export type PQCandidateStatus = (typeof PQCANDIDATE_STATUS)[keyof typeof PQCANDIDATE_STATUS];

/**
 * Job status enum
 */
export type JobStatus = "CREATED" | "IN_PROGRESS" | "FAILED" | "NEEDS_REVIEW" | "FINALIZED";

/**
 * PQ Ingestion Job
 */
export type PQIngestionJob = {
  id: string;
  original_filename: string;
  additional_input: string | null;
  status: JobStatus;
  retriable: boolean;
  parent_job_id: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Paginated response for PQ Ingestion Jobs
 * Supports both flat structure and nested page structure
 */
export type PQIngestionJobsResponse = {
  content: PQIngestionJob[];
  page?: {
    size: number;
    number: number;
    total_elements: number;
    total_pages: number;
  };
  // Legacy flat structure (for backward compatibility)
  total_pages?: number;
  total_elements?: number;
  size?: number;
  number?: number;
  number_of_elements?: number;
  first?: boolean;
  last?: boolean;
  empty?: boolean;
  pageable?: {
    offset: number;
    paged: boolean;
    page_number: number;
    page_size: number;
    sort?: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    unpaged: boolean;
  };
  sort?: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
};

/**
 * Response from file upload to S3
 */
export type UploadResponse = {
  s3_key: string;
  original_filename: string;
  file_size: number;
  uploaded_at: string;
  is_existing: boolean;
};

/**
 * Response from creating a PQ Ingestion Job
 */
export type JobResponse = {
  id: string;
  original_filename: string;
  additional_input: string;
  status: JobStatus;
  retriable: boolean;
  parent_job_id: string | null;
  ingestion_result: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * Payload for uploading an attachment
 */
export type UploadAttachmentPayload = {
  file: string; // base64 encoded file
  filename: string;
  content_type: string;
  file_size: number;
};

/**
 * Availability status of the stock as stored in Salesforce.
 * Maps user-facing labels to backend picklist values.
 */
export enum TypeOfStockEnum {
  IN_MY_WAREHOUSE = "in_my_warehouse",
  ORDERED_IN_TRANSIT = "ordered_in_transit",
  ORDERED = "ordered",
  AVAILABLE_TO_ORDER = "available_to_order",
}

/**
 * Source of quotation enum — identifies how the PQ was created:
 * - "supplier-app"  → normal supplier logged in directly
 * - "salesforce"    → Andercore employee using Switch Supplier / impersonation
 */
export enum SourceOfQuotationEnum {
  SUPPLIER_APP = "supplier-app",
  SALESFORCE = "salesforce",
}

/**
 * Payload for creating a job
 */
export type CreateJobPayload = {
  s3_key: string;
  original_filename: string;
  additional_input: string | null;
  pq_type: AvailabilityTypeEnum;
  type_of_stock: TypeOfStockEnum;
  delivery_time_in_days?: number;
  earliest_availability_date?: string;
  source: SourceOfQuotationEnum;
  user: string;
};

/**
 * Query parameters for fetching jobs
 */
export type FetchJobsParams = {
  supplier_ids?: string;
  status?: string;
  limit?: number;
  page?: number;
};

/**
 * UI Status for display purposes
 */
export type UIStatus = "FINALIZED" | "NEEDS_REVIEW" | "IN_PROGRESS" | "FAILED";

/**
 * Submission record for table display
 */
export type SubmissionRecord = {
  id: string;
  fileName: string;
  submittedAt: string;
  status: UIStatus;
  lastUpdate: string;
  actionLabel: string;
  retriable?: boolean;
  parentJobId?: string | null;
};

/**
 * Salesforce Product information
 */
export type SalesforceProduct = {
  id: string;
  name: string;
  is_active: boolean;
};

/**
 * Product search result
 */
export type ProductSearchResult = {
  id: string;
  record_type: string;
  name: string;
  brand: string;
  model: string;
  status: string;
  image_url: string;
  units_per_pallet: number;
  units_per_container: number;
  /** Product category (ProductCategory enum value) - used to show/hide Price per Wattpeak for PV Modules */
  category?: string;
};

/**
 * PQ Candidate from ingestion result
 */
export type PQCandidate = {
  id?: string; // Optional ID from API response
  comments: string | null;
  incoterm: string;
  unit_price: string;
  valid_until: string | null;
  unit_price_pw: string | null;
  parsed_product: string;
  salesforce_product: SalesforceProduct | null;
  relevant_sf_products?: ProductSearchResult[] | null;
  incoterm_location_city: string;
  minimum_order_quantity: number | null;
  earliest_available_date: string;
  incoterm_location_country: string;
  incoterm_location_id?: string | null;
  incoterm_location_postal_code?: string | null;
  incoterm_location_street?: string | null;
  maximum_available_quantity: number;
  /** Stock availability type: ALWAYS_IN_STOCK, UPCOMING_STOCK, IN_STOCK_NOW */
  pq_type?: AvailabilityTypeEnum | null;
  /** Lead time in days when pq_type is ALWAYS_IN_STOCK */
  delivery_time_in_days?: number | null;
  error_details?: {
    salesforce?: string;
  } | null;
};

/**
 * Ingestion result owner information
 */
export type IngestionResultOwner = {
  sf_user_id: string;
};

/**
 * Ingestion result supplier information
 */
export type IngestionResultSupplier = {
  sf_id: string;
};

/**
 * Complete ingestion result structure
 */
export type IngestionResult = {
  owner: IngestionResultOwner;
  status: string;
  supplier: IngestionResultSupplier;
  pq_candidates: PQCandidate[];
};

/**
 * Detailed job response with ingestion result
 */
export type JobDetailResponse = {
  id: string;
  original_filename: string;
  additional_input: string | null;
  status: JobStatus;
  retriable: boolean;
  parent_job_id: string | null;
  ingestion_result: IngestionResult | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};

/**
 * PQ Candidate from API (with all fields)
 */
export type PQCandidateFromAPI = {
  id: string;
  job_id: string;
  status: string;
  incoterm: string;
  incoterm_location_city: string;
  incoterm_location_country: string;
  incoterm_location_id?: string | null;
  incoterm_location_postal_code?: string | null;
  incoterm_location_street?: string | null;
  minimum_order_quantity: number;
  maximum_available_quantity: number;
  unit_purchase_price: number;
  valid_until: string | null;
  earliest_availability_date: string;
  pq_type: string | null;
  /** Lead time in days when pq_type is ALWAYS_IN_STOCK */
  delivery_time_in_days?: number | null;
  parsed_product_name: string;
  sf_product: Record<string, unknown> | null;
  relevant_sf_products: ProductSearchResult[] | null;
  error_details: Record<string, string> | null;
  created_at: string;
  updated_at: string;
  version: number;
};

/**
 * Paginated response for PQ Candidates
 */
export type PQCandidatesPaginatedResponse = {
  total_elements: number;
  total_pages: number;
  size: number;
  content: PQCandidateFromAPI[];
  number: number;
  number_of_elements: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  pageable: {
    offset: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    paged: boolean;
    page_number: number;
    page_size: number;
    unpaged: boolean;
  };
  first: boolean;
  last: boolean;
  empty: boolean;
};

/**
 * Response wrapper for PQ candidates endpoint
 * Contains paginated candidates and status counts
 */
export type PQCandidatesResponse = {
  candidates: PQCandidatesPaginatedResponse;
  counts: {
    NOT_CREATED: number;
    IN_REVIEW: number;
    CREATED: number;
    FAILED: number;
  };
  finalized: boolean; // Whether the job is finalized (no fields editable)
};

/**
 * Product information from PQ API
 */
export type PQProduct = {
  name: string;
  color: string;
  serial_number: string;
};

/**
 * Stock item type from PQ API
 */
export type StockItemType = {
  id: string;
  name: string;
  incoterm: string;
  category: string;
  minimum_order_quantity: number;
  maximum_available_quantity: number;
  unit_purchase_price: number;
  price_per_watt: number | null;
  valid_until: string;
  earliest_availability_date: string;
  created_date: string;
  product: PQProduct;
  image_url: string;
  sf_product: SalesforceProduct;
  product_module_surface_type: string | null;
  product_glass_type: string | null;
  product_model_series: string | null;
  sales_volume: number;
  sales_volume_all_time: number;
  status: string;
  supplier_account_id: string;
  incoterm_location_city: string;
  incoterm_location_country: string;
  incoterm_location_id?: string | null;
  incoterm_location_postal_code?: string | null;
};

/**
 * Paginated response from PQ API for stock list
 */
export type StockListPaginatedResponse = {
  content: StockItemType[];
  page: {
    size: number;
    number: number;
    total_elements: number;
    total_pages: number;
  };
};

/**
 * Response from GET /pq/{id} - full PQ detail
 */
export type PQDetailResponse = {
  id: string;
  name: string;
  incoterm: string;
  minimum_order_quantity: number;
  maximum_available_quantity: number;
  unit_purchase_price: number;
  price_per_watt: number | null;
  created_date: string;
  valid_until: string;
  earliest_availability_date: string | null;
  lead_time?: number;
  sf_product: {
    id: string;
    name: string;
    is_active: boolean;
  };
  product_glass_type: string | null;
  product_module_surface_type: string | null;
  product_model_series: string | null;
  image_url: string | null;
  category: string;
  sales_volume_this_year?: number;
  sales_volume_all_time?: number;
  status: string;
  record_type?: string;
  supplier_account_id: string;
  incoterm_location_city: string;
  incoterm_location_country: string;
  incoterm_location_street?: string;
  incoterm_location_postal_code?: string;
  incoterm_location_id?: string | null;
  units_per_pallet?: number;
  units_per_container?: number;
  includes_logistics_cost?: boolean;
  pq_type?: AvailabilityTypeEnum;
};

/**
 * Payload item for bulk updating PQs via PATCH /pq/bulk-update
 * Only `id` is required; all other fields are optional partial updates.
 */
export type BulkUpdatePQItem = {
  id: string;
  incoterm?: string;
  incoterm_location_city?: string;
  incoterm_location_country?: string;
  incoterm_location_street?: string;
  incoterm_location_postal_code?: string;
  incoterm_location_id?: string;
  minimum_order_quantity?: number;
  maximum_available_quantity?: number;
  unit_purchase_price?: number;
  valid_until?: string;
};

/**
 * Payload for creating a single product offering via POST /pq
 */
export type CreateSingleOfferingPayload = {
  minimum_order_quantity: number;
  pq_type: AvailabilityTypeEnum;
  maximum_available_quantity: number;
  unit_purchase_price?: number;
  price_per_watt?: number;
  valid_until: string;
  earliest_availability_date?: string;
  lead_time?: number;
  sf_product: {
    name: string;
    id: string;
    is_active: boolean;
  };
  incoterm: string;
  incoterm_location_street: string;
  incoterm_location_postal_code: string;
  incoterm_location_city: string;
  incoterm_location_country: string;
  incoterm_location_id?: string;
  includes_logistics_cost?: boolean;
};
